import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings, Database, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import type { ERPConfig } from '../../services/erpIntegration';

interface ERPConfigPanelProps {
  onConfigSave: (config: ERPConfig) => void;
  onTestConnection: (config: ERPConfig) => Promise<boolean>;
  onSync: (config: ERPConfig) => Promise<void>;
}

export const ERPConfigPanel: React.FC<ERPConfigPanelProps> = ({
  onConfigSave,
  onTestConnection,
  onSync
}) => {
  const [config, setConfig] = useState<ERPConfig>({
    type: 'custom',
    endpoint: '',
    apiKey: '',
    username: '',
    password: '',
    syncInterval: 300,
    fieldMappings: {},
    enabled: false
  });
  
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'success' | 'error'>('idle');
  const [lastSync, setLastSync] = useState<string | null>(null);

  const erpTypes = [
    { value: 'sap', label: 'SAP S/4HANA' },
    { value: 'oracle', label: 'Oracle NetSuite' },
    { value: 'dynamics', label: 'Microsoft Dynamics 365' },
    { value: 'netsuite', label: 'NetSuite' },
    { value: 'fishbowl', label: 'Fishbowl Inventory' },
    { value: 'cin7', label: 'Cin7 Core' },
    { value: 'custom', label: 'Custom API' }
  ];

  const handleTestConnection = async () => {
    setConnectionStatus('testing');
    try {
      const success = await onTestConnection(config);
      setConnectionStatus(success ? 'success' : 'error');
    } catch (error) {
      setConnectionStatus('error');
    }
  };

  const handleSync = async () => {
    setSyncStatus('syncing');
    try {
      await onSync(config);
      setSyncStatus('success');
      setLastSync(new Date().toISOString());
    } catch (error) {
      setSyncStatus('error');
    }
  };

  const handleSave = () => {
    onConfigSave(config);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'testing':
      case 'syncing':
        return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Database className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <Settings className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">ERP Integration</h2>
          <p className="text-gray-600">Configure connection to your ERP/WMS system</p>
        </div>
      </div>

      {/* Connection Status */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Connection Status</h3>
          <div className="flex items-center space-x-2">
            {getStatusIcon(connectionStatus)}
            <Badge variant={
              connectionStatus === 'success' ? 'success' :
              connectionStatus === 'error' ? 'error' :
              connectionStatus === 'testing' ? 'warning' : 'neutral'
            }>
              {connectionStatus === 'idle' ? 'Not Tested' :
               connectionStatus === 'testing' ? 'Testing...' :
               connectionStatus === 'success' ? 'Connected' : 'Failed'}
            </Badge>
          </div>
        </div>

        {lastSync && (
          <p className="text-sm text-gray-600">
            Last sync: {new Date(lastSync).toLocaleString()}
          </p>
        )}
      </Card>

      {/* Configuration Form */}
      <Card>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Configuration</h3>
        
        <div className="space-y-4">
          {/* ERP Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ERP System Type
            </label>
            <select
              value={config.type}
              onChange={(e) => setConfig({ ...config, type: e.target.value as any })}
              className="w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
            >
              {erpTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Endpoint URL */}
          <Input
            label="API Endpoint URL"
            value={config.endpoint}
            onChange={(e) => setConfig({ ...config, endpoint: e.target.value })}
            placeholder="https://api.yourerp.com/v1"
          />

          {/* Authentication */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="API Key"
              type="password"
              value={config.apiKey}
              onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
              placeholder="Your API key"
            />
            
            <Input
              label="Username (if required)"
              value={config.username || ''}
              onChange={(e) => setConfig({ ...config, username: e.target.value })}
              placeholder="Username"
            />
          </div>

          {config.username && (
            <Input
              label="Password"
              type="password"
              value={config.password || ''}
              onChange={(e) => setConfig({ ...config, password: e.target.value })}
              placeholder="Password"
            />
          )}

          {/* Sync Interval */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sync Interval (seconds)
            </label>
            <input
              type="number"
              value={config.syncInterval}
              onChange={(e) => setConfig({ ...config, syncInterval: parseInt(e.target.value) })}
              className="w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              min="60"
              max="3600"
            />
          </div>

          {/* Enable/Disable */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="enabled"
              checked={config.enabled}
              onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
              Enable automatic synchronization
            </label>
          </div>
        </div>
      </Card>

      {/* Field Mappings */}
      <Card>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Field Mappings</h3>
        <p className="text-sm text-gray-600 mb-4">
          Map your ERP fields to warehouse system fields
        </p>
        
        <div className="space-y-3">
          {[
            { local: 'orderNumber', label: 'Order Number', placeholder: 'order_id' },
            { local: 'customerName', label: 'Customer Name', placeholder: 'customer_name' },
            { local: 'productSku', label: 'Product SKU', placeholder: 'item_code' },
            { local: 'quantity', label: 'Quantity', placeholder: 'qty' },
            { local: 'binLocation', label: 'Bin Location', placeholder: 'location_code' }
          ].map(field => (
            <div key={field.local} className="grid grid-cols-2 gap-4 items-center">
              <label className="text-sm font-medium text-gray-700">
                {field.label}
              </label>
              <input
                type="text"
                value={config.fieldMappings[field.local] || ''}
                onChange={(e) => setConfig({
                  ...config,
                  fieldMappings: {
                    ...config.fieldMappings,
                    [field.local]: e.target.value
                  }
                })}
                placeholder={field.placeholder}
                className="h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>
          ))}
        </div>
      </Card>

      {/* Actions */}
      <div className="flex space-x-3">
        <Button
          onClick={handleTestConnection}
          variant="outline"
          loading={connectionStatus === 'testing'}
          disabled={!config.endpoint || !config.apiKey}
        >
          Test Connection
        </Button>
        
        <Button
          onClick={handleSync}
          variant="secondary"
          loading={syncStatus === 'syncing'}
          disabled={connectionStatus !== 'success' || !config.enabled}
        >
          Sync Now
        </Button>
        
        <Button
          onClick={handleSave}
          variant="primary"
          disabled={!config.endpoint || !config.apiKey}
        >
          Save Configuration
        </Button>
      </div>
    </div>
  );
};